import type { CSSProperties } from 'react'
import { useCallback, useState } from 'react'

import { updateRundownCue } from '@/firestore'
import { tz } from '@date-fns/tz'
import { differenceInCalendarDays } from 'date-fns'
import { useAtomValue } from 'jotai'
import _includes from 'lodash/includes'
import _intersection from 'lodash/intersection'

import useDeepCompareMemo from '@/utils/useDeepCompareMemo'

import { CueStartMode } from '~shared/rundown/cue'
import { RundownPermission } from '~shared/rundown/permission'
import { applyDate } from '~shared/timeutils'
import { CueRunState, fastDeepEqual } from '~shared/utils'

import { runnerStateAtom } from '@/store/runner.store'

import { useRundownPermission } from '@/features/permissions/lib/use-rundown-permission'
import { useRundown } from '@/features/rundown/rundown.derived'

import { useCuesDerived } from '../cue.derived'
import { useCue, useCueTimestamp, useNextCue } from '../cue.hooks'
import { SubTimerList } from '../sub-timers/SubTimerList'

import { Duration } from './Duration'
import { StartTime } from './StartTime'
import type { TimingValues } from './timing.types'
import { TimingScaffold } from './TimingScaffold'

type TimingProps = {
  rundownId: string
  cueId: string
  timezone?: string
  cellStyle?: CSSProperties
  skipDuringShow?: boolean
}

/**
 * Timing Component
 *
 * Displays and manages time-related information for a cue, including:
 *
 * - Start time (with scheduling options)
 * - Duration
 * - Time variance indicators
 *
 * The component handles both display and editing of timing information,
 * synchronizing with Firestore for persistence.
 */
export function Timing({
  rundownId,
  cueId,
  timezone = 'UTC',
  cellStyle,
  skipDuringShow,
}: TimingProps) {
  const rundown = useRundown()
  const rundownStartTime = rundown.startTime
  const todDisplayFormat = rundown.settings.todDisplayFormat

  const runnerState = useAtomValue(runnerStateAtom)

  const cue = useCue(cueId)
  const timestamp = useCueTimestamp(cueId)
  const nextCue = useNextCue(cueId)
  const { firstCueId } = useCuesDerived()
  const isFirstCue = cueId === firstCueId

  // The first cue is always treated as a hard start
  // regardless of its persisted startMode
  const isSoftCue = !isFirstCue && cue?.startMode === CueStartMode.FLEXIBLE

  // UI state
  const [loading, setLoading] = useState<string[]>([])

  const isEditor = useRundownPermission({
    permission: RundownPermission.EDIT,
  })
  const isShowCaller = useRundownPermission({
    permission: RundownPermission.SHOWCALL,
  })
  // Access and editing state
  const readonly =
    cue?.settings?.preventEdits ||
    timestamp?.state === CueRunState.CUE_PAST ||
    !isEditor

  // Extract only the needed timestamp properties to avoid unnecessary recalculations
  const timestampOriginalStart = timestamp?.original?.start
  const timestampActualStart = timestamp?.actual?.start

  /**
   * Calculate effective values that will be passed down.
   * This includes any derived or computed values.
   *
   * - First cue startTime comes from rundown and is always FIXED
   *
   * Using useDeepCompareMemo for both preflight (dependency) and postflight
   * (result) comparison to avoid unnecessary reference changes when data is
   * deeply equal.
   */
  const effectiveValues = useDeepCompareMemo(() => {
    if (!cue) return null

    const originalStart =
      timestampOriginalStart || (cue.startTime ?? new Date(0))
    const actualStart = timestampActualStart || (cue.startTime ?? new Date(0))

    // Start Time
    // Soft cues have no persisted startTime — their timing is computed from
    // preceding cues and lives in the `timestamp` prop. Keeping null here
    // ensures handleUpdate's change detection sees a real diff when switching
    // a soft cue to FIXED mode (StartTime.onPropChange sets the value).
    let startTime: Date | null = cue.startTime
      ? applyDate(cue.startTime, originalStart, { timezone })
      : originalStart
    // Use the cue's own startTime (with the rundown date) for the first cue,
    // not rundown.startTime directly — they can be out of sync when the user
    // changes the date and time separately, causing the "time is in the past"
    // check to evaluate against a stale rundown.startTime.
    if (isFirstCue) {
      startTime = cue.startTime
        ? applyDate(cue.startTime, rundownStartTime, {
            timezone,
          })
        : rundownStartTime
    }
    if (isSoftCue) startTime = null

    // Start Mode
    let startMode = cue.startMode
    if (isFirstCue) startMode = CueStartMode.FIXED

    // Start Date Plus
    let startDatePlus = cue.startDatePlus
    if (isFirstCue) startDatePlus = 0
    if (isSoftCue)
      startDatePlus = differenceInCalendarDays(actualStart, rundownStartTime, {
        in: tz(timezone),
      })

    return {
      startTime,
      startMode,
      startDatePlus,
      scheduled: cue.scheduled,
      duration: cue.duration,
    }
  }, [
    cue,
    rundownStartTime,
    isFirstCue,
    isSoftCue,
    timestampOriginalStart,
    timestampActualStart,
    timezone,
  ])

  /**
   * Updates the cue and (for first cue) the rundown
   *
   * - Determine which properties are being updated
   * - Bail early: Skip API call if no actual changes
   * - Add all changed props to loading state
   * - Special handling for first cue
   */
  const handleUpdate = useCallback(
    async (update: Partial<TimingValues>) => {
      if (!cue || !effectiveValues) return

      // Determine which properties actually changed
      const changes: Record<string, unknown> = {}
      Object.entries(update).forEach(([key, value]) => {
        if (!fastDeepEqual(value, effectiveValues[key])) {
          changes[key] = value
        }
      })

      // Bail if no actual changes
      if (Object.keys(changes).length === 0) return

      // Track loading state for changed properties
      setLoading(Object.keys(changes))
      try {
        await updateRundownCue(rundownId, cue.id, changes, isFirstCue)
      } catch (error) {
        console.error('Failed to update cue timing:', error)
        // Error handling is done by updateRundownCue (shows toast)
      } finally {
        setLoading([])
      }
    },
    [effectiveValues, rundownId, cue, isFirstCue],
  )

  if (!cue || !effectiveValues) return null

  return (
    <>
      {/* Start Time */}
      {skipDuringShow ? (
        <TimingScaffold style={cellStyle}>
          <div className="min-h-max max-h-full h-6 px-1.5 w-full rounded-sm bg-black/70 flex items-center justify-center">
            <span className="opacity-50 tabular-nums text-sm">--:--</span>
          </div>
        </TimingScaffold>
      ) : (
        <StartTime
          cueId={cue.id}
          values={effectiveValues}
          setValues={handleUpdate}
          readonly={readonly}
          loading={
            _intersection(loading, [
              'startTime',
              'startDatePlus',
              'startMode',
              'scheduled',
            ]).length > 0
          }
          timestamp={timestamp}
          timezone={timezone}
          isFirstCue={isFirstCue}
          cellStyle={cellStyle}
          timingProps={{
            todDisplayFormat,
            runnerState: runnerState,
            rundownStartTime,
          }}
          nextCue={nextCue}
        />
      )}

      {/* Duration */}
      <Duration
        values={effectiveValues}
        setValues={handleUpdate}
        readonly={readonly}
        loading={_includes(loading, 'duration')}
        timestamp={timestamp}
        timezone={timezone}
        cellStyle={cellStyle}
        below={
          <SubTimerList
            rundownId={rundownId}
            cueId={cue.id}
            canEdit={isEditor && !cue?.settings?.preventEdits}
            canRun={isShowCaller}
          />
        }
      />
    </>
  )
}
